// src/components/sections/HomeTailoredSolutions.jsx
import React, { useRef } from 'react'
import { motion as Motion, useInView } from 'framer-motion'
import { LazyImage } from '../common/LazyImage.jsx'
import './HomeTailoredSolutions.scss'

export default function HomeTailoredSolutions({ sectionData }) {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { amount: 0.2 })

  // Skeleton (same idea as your v2, just with new wrapper classes)
  if (!sectionData) {
    return (
      <section className="industry-solutions" ref={sectionRef}>
        <div className="container">
          <div className="section-header animate-pulse">
            <div className="h-8 bg-black-200 rounded w-1/3 mx-auto mb-4" />
            <div className="h-4 bg-black-200 rounded w-1/2 mx-auto" />
          </div>
          <div className="industries-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="industry-card">
                <div className="industry-icon">
                  <div className="w-16 h-16 bg-black-200 rounded-full mx-auto animate-pulse" />
                </div>
                <div className="h-4 bg-black-200 rounded w-2/3 mx-auto" />
              </div>
            ))}
          </div>
          <button className="cta-button animate-pulse h-10 w-56 mx-auto" />
        </div>
      </section>
    )
  }

  const { heading, subheading, cards = [], buttonText } = sectionData

  if (cards.length === 0) {
    return null
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: 'beforeChildren',
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <section className="industry-solutions" ref={sectionRef}>
      <div className="container">
        {/* Heading */}
        <Motion.h2
          className="section-heading"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={itemVariants}
        >
          {heading ? heading.toUpperCase() : ''}
        </Motion.h2>

        {/* Optional subheading under the heading (v2 data, extra line) */}
        {subheading && (
          <Motion.p
            className="section-subheading"
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={itemVariants}
          >
            {subheading}
          </Motion.p>
        )}

        {/* Cards grid */}
        <Motion.div
          className="industries-grid"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          {cards.map((card, index) => (
            <Motion.div
              key={index}
              className="industry-card"
              variants={itemVariants}
            >
              <div className="industry-icon">
                <LazyImage
                  src={card.imageUrl}
                  alt={card.title}
                  className="w-16 h-16 mx-auto object-contain"
                  placeholder={
                    <div className="w-16 h-16 bg-black-200 rounded-full mx-auto" />
                  }
                />
              </div>
              <h3>{card.title}</h3>
              {card.subtitle && (
                <p className="industry-subtitle">{card.subtitle}</p>
              )}
            </Motion.div>
          ))}
        </Motion.div>

        {/* CTA button – behavior stays same as v2 (just renders text) */}
        <Motion.button
          className="cta-button"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={itemVariants}
          transition={{ delay: 0.3 }}
        >
          {buttonText || 'Learn more about industries'}
        </Motion.button>
      </div>
    </section>
  )
}
